import React, { useState, useRef, useEffect } from 'react';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useAuth } from '../../contexts/AuthContext';
import { X, Mail, Lock, ArrowRight, Loader2, AlertTriangle, CheckCircle } from 'lucide-react';
import { testSupabaseConnection } from '../../services/supabase';
import { signInWithProvider } from '../../services/auth';
import { useScheduleStore } from '../../store';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultMode?: 'signin' | 'signup';
}

// A stalled auth request must not leave the button spinning forever -
// give the server a generous window, then surface a retryable error.
const AUTH_TIMEOUT_MS = 15000;

const withTimeout = <T,>(promise: Promise<T>): Promise<T> =>
  Promise.race([
    promise,
    new Promise<never>((_, reject) =>
      setTimeout(
        () => reject(new Error('The server is taking too long to respond. Please check your connection and try again.')),
        AUTH_TIMEOUT_MS
      )
    ),
  ]);

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, defaultMode = 'signin' }) => {
  const [mode, setMode] = useState<'signin' | 'signup' | 'forgot'>(defaultMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<{ message: string; type?: string } | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'checking' | 'connected' | 'error'>('checking');
  const { signIn, signUp, resetPassword } = useAuth();
  const mounted = useRef(true);
  const trapRef = useFocusTrap<HTMLDivElement>();
  // Get the schedule to check if there's data to migrate
  const schedule = useScheduleStore(state => state.schedule);
  const hasScheduleData = schedule && schedule.timeBlocks.length > 0;

  useEffect(() => {
    // Re-arm on every (re)run - StrictMode runs effect cleanup between
    // double-invocations, so a one-time useRef(true) would stay false and
    // silently swallow all guarded state updates (spinner never clears).
    mounted.current = true;

    // Check connection when modal opens
    if (isOpen) {
      checkConnection();
    }

    return () => {
      mounted.current = false;
    };
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkConnection = async () => {
    if (!isOpen) return;
    
    setConnectionStatus('checking');
    const isConnected = await testSupabaseConnection();
    
    if (mounted.current) {
      setConnectionStatus(isConnected ? 'connected' : 'error');
      if (!isConnected) {
        setError({
          message: 'Unable to connect to the authentication service. This may be because the database was recently paused and resumed. Please wait a few minutes and try again, or check your internet connection.',
          type: 'network'
        });
      } else {
        // Clear any network-related errors if connection is successful
        if (error?.type === 'network') {
          setError(null);
        }
      }
    }
  };

  if (!isOpen) return null;

  const validateSignUp = () => {
    if (password.length < 8) {
      setError({ message: 'Password must be at least 8 characters long', type: 'password' });
      return false;
    }
    if (password !== confirmPassword) {
      setError({ message: 'Passwords do not match', type: 'confirm_password' });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    // If we already know there's a connection issue, don't attempt to sign in
    if (connectionStatus === 'error') {
      setError({
        message: 'Unable to connect to the authentication service. Please check your internet connection and try again.',
        type: 'network'
      });
      setLoading(false);
      return;
    }

    try {
      if (mode === 'forgot') {
        try {
          await withTimeout(resetPassword(email));
          setSuccess('Check your email for a link to reset your password.');
        } catch (err) {
          setError({ message: err instanceof Error ? err.message : 'Failed to send reset email. Please try again.' });
        }
        return;
      }

      if (mode === 'signup' && !validateSignUp()) {
        setLoading(false);
        return;
      }

      let result;
      try {
        result = mode === 'signin'
          ? await withTimeout(signIn(email, password))
          : await withTimeout(signUp(email, password));
      } catch (err) {
        if (!mounted.current) return;
        setError({
          message: err instanceof Error ? err.message : 'Something went wrong. Please try again.',
          type: 'network',
        });
        return;
      }

      if (!mounted.current) return;

      if (result.error) {
        setError(result.error);
        
        if (result.error.type === 'network') {
          setConnectionStatus('error');
        }
        
        return;
      }

      // Email confirmation required - don't close, show instructions
      if ('requiresEmailConfirmation' in result && result.requiresEmailConfirmation) {
        setSuccess('Account created! Please check your email and click the confirmation link to activate your account.');
        return;
      }

      // Signed in immediately - close modal
      if (hasScheduleData) {
        setSuccess('Your existing schedule data has been saved to your account!');
        setTimeout(() => {
          if (mounted.current) {
            onClose();
          }
        }, 2000);
      } else {
        onClose();
      }

    } finally {
      if (mounted.current) {
        setLoading(false);
      }
    }
  };

  return (
    <div className="modal-backdrop p-4" role="dialog" aria-modal="true" aria-labelledby="auth-modal-title">
      <div ref={trapRef} className="modal-content card w-full max-w-md p-8 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-secondary transition-colors"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="text-center mb-8">
          <h2 id="auth-modal-title" className="text-2xl font-bold">
            {mode === 'signin' ? 'Welcome Back' : mode === 'signup' ? 'Create Your Account' : 'Reset Your Password'}
          </h2>
          <p className="text-muted-foreground mt-2">
            {mode === 'signin'
              ? 'Sign in to access your custody schedule'
              : mode === 'signup'
                ? 'Get started with your custody planning journey'
                : "Enter your email and we'll send you a reset link"}
          </p>
        </div>

        {connectionStatus === 'error' && (
          <div className="px-4 py-3 rounded-lg mb-6 text-sm bg-secondary border text-muted-foreground flex items-center">
            <AlertTriangle className="w-4 h-4 mr-2 flex-shrink-0" />
            <span>
              Connection issue detected. This may be because the Supabase database was recently resumed. Please wait a few minutes and try again, or check your internet connection.
              <button 
                onClick={checkConnection}
                className="ml-2 underline font-medium"
              >
                Retry
              </button>
            </span>
          </div>
        )}

        {error && (
          <div className={`px-4 py-3 rounded mb-6 text-sm ${
            error.type === 'success'
              ? 'bg-pb-tint border border-transparent text-pb-text'
              : error.type === 'network'
                ? 'bg-warn-tint border border-warn/30 text-warn'
                : 'bg-bad-tint border border-transparent text-bad'
          }`}>
            {error.message}
          </div>
        )}

        {success && (
          <div className="px-4 py-3 rounded mb-6 text-sm bg-pb-tint border border-transparent text-pb-text flex items-center">
            <CheckCircle className="w-4 h-4 mr-2 flex-shrink-0" />
            <span>{success}</span>
          </div>
        )}

        {/* Show note about data migration if user has local schedule data */}
        {hasScheduleData && !success && mode !== 'forgot' && (
          <div className="px-4 py-3 rounded-lg mb-6 text-sm bg-primary/10 border border-primary/20 text-primary">
            <p>
              <span className="font-medium">Note:</span> Your current schedule will be automatically saved to your account when you {mode === 'signin' ? 'sign in' : 'create an account'}.
            </p>
          </div>
        )}

        {/* OAuth providers */}
        <div className={`space-y-3 mb-6 ${mode === 'forgot' ? 'hidden' : ''}`}>
          <button
            type="button"
            onClick={() => signInWithProvider('google')}
            className="w-full h-11 flex items-center justify-center gap-3 rounded-md border border-input bg-background hover:bg-secondary transition-colors text-sm font-medium"
            aria-label="Continue with Google"
          >
            <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </button>

        </div>

        {mode !== 'forgot' && (
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-input" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">or continue with email</span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="auth-email" className="block text-sm font-medium mb-2">
              Email Address
            </label>
            <div className="flex items-center w-full rounded-md border border-input bg-background ring-offset-background">
              <div className="flex items-center justify-center px-3 text-muted-foreground">
                <Mail className="w-5 h-5" />
              </div>
              <input
                id="auth-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex w-full h-11 rounded-md bg-transparent px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 border-0"
                placeholder="Enter your email"
                required
                autoComplete="email"
              />
            </div>
          </div>

          {mode !== 'forgot' && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="auth-password" className="block text-sm font-medium">
                  Password
                </label>
                {mode === 'signin' && (
                  <button
                    type="button"
                    onClick={() => {
                      setMode('forgot');
                      setError(null);
                      setSuccess(null);
                      setPassword('');
                    }}
                    className="text-xs text-primary hover:underline font-medium"
                  >
                    Forgot password?
                  </button>
                )}
              </div>
              <div className="flex items-center w-full rounded-md border border-input bg-background ring-offset-background">
                <div className="flex items-center justify-center px-3 text-muted-foreground">
                  <Lock className="w-5 h-5" />
                </div>
                <input
                  id="auth-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="flex w-full h-11 rounded-md bg-transparent px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 border-0"
                  placeholder="Enter your password"
                  required
                  autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                />
              </div>
            </div>
          )}

          {mode === 'signup' && (
            <div>
              <label htmlFor="auth-confirm-password" className="block text-sm font-medium mb-2">
                Confirm Password
              </label>
              <div className="flex items-center w-full rounded-md border border-input bg-background ring-offset-background">
                <div className="flex items-center justify-center px-3 text-muted-foreground">
                  <Lock className="w-5 h-5" />
                </div>
                <input
                  id="auth-confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="flex w-full h-11 rounded-md bg-transparent px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 border-0"
                  placeholder="Confirm your password"
                  required
                  autoComplete="new-password"
                />
              </div>
            </div>
          )}

          <button
            type="submit"
            className="button button-primary w-full h-11 flex items-center justify-center gap-2"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {mode === 'signin' ? 'Signing in...' : mode === 'signup' ? 'Creating account...' : 'Sending email...'}
              </>
            ) : (
              <>
                {mode === 'signin' ? 'Sign In' : mode === 'signup' ? 'Create Account' : 'Send Reset Link'}
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <div className="text-sm text-muted-foreground">
            {mode === 'signin' ? (
              <>
                Don't have an account?{' '}
                <button
                  onClick={() => {
                    setMode('signup');
                    setError(null);
                    setSuccess(null);
                    setPassword('');
                    setConfirmPassword('');
                  }}
                  className="text-primary hover:underline font-medium"
                >
                  Sign up
                </button>
              </>
            ) : mode === 'signup' ? (
              <>
                Already have an account?{' '}
                <button
                  onClick={() => {
                    setMode('signin');
                    setError(null);
                    setSuccess(null);
                    setPassword('');
                    setConfirmPassword('');
                  }}
                  className="text-primary hover:underline font-medium"
                >
                  Sign in
                </button>
              </>
            ) : (
              <>
                Remember your password?{' '}
                <button
                  onClick={() => {
                    setMode('signin');
                    setError(null);
                    setSuccess(null);
                  }}
                  className="text-primary hover:underline font-medium"
                >
                  Back to sign in
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;